<template>
  <section>
    <FormText
      label="Name"
      class="mb-2"
      required
      :disabled="disabled"
      :value="value.name"
      @input="update('name', $event)"
    />
    <FormText
      label="Description"
      class="mb-2"
      :disabled="disabled"
      :value="value.description"
      @input="update('description', $event)"
    />
    <FormSelect
      label="Type"
      required
      :config="{ options: courseSetOptions }"
      :customLabel="decamelize"
      :disabled="disabled"
      :value="value.type"
      @input="update('type', $event)"
    />
    <div
      v-if="value.type === 'static'"
      class="mt-2"
    >
      <strong>Setup static {{ $localize('course set') }}</strong>
      <LogicTree
        class="mt-2"
        :value="courseSetStructure"
        :editable="true"
        @input="update('structure', $event)"
      >
        <template #default="{ value:rule, update:updateStructure, editable:editableStructure }">
          <FormCourseSelect
            :placeholder="`Select ${ $localize('course') }`"
            :value="rule.value"
            :disabled="!editableStructure"
            :config="{ multiple: false, returnLatestRevision: true }"
            @input="updateStructure({ value: $event })"
          />
        </template>
      </LogicTree>
    </div>
    <div
      v-if="value.type === 'dynamic'"
      class="mt-2"
    >
      <strong>Set up dynamic {{ $localize('course set') }}</strong>
      <LogicTree
        class="mt-2"
        :value="courseSetStructure"
        :editable="true"
        :defaultNewItem="newDynamicFilter"
        @input="update('structure', $event)"
      >
        <template #default="{ value:rule, update:updateStructure, editable:editableStructure }">
          <DynamicFilter
            :value="rule"
            view="cm"
            :fields="filterFields"
            :editable="!disabled && !isLoadingTemplate && editableStructure"
            class="flex-1"
            @input="updateStructure"
          />
        </template>
      </LogicTree>
    </div>
    <div
      v-if="value.type === 'dynamic' && value.dynamicCourseList && value.dynamicCourseList.length"
      class="pt-4"
    >
      <h4>{{ $localize('courses') | capitalize }} Table ({{ value.dynamicCourseList.length }})</h4>
      <div class="pb-4">
        Note: The {{ $localize('courses') | capitalize }} table will be updated after every save. (Refresh to see the updated list)
      </div>
      <AsyncPagination
        :listLength="value.dynamicCourseList.length"
        :per="pagination.limit"
        :currentPage="pagination.currentPage"
        buttonsClass="justify-content-end"
        class="flush pl-2"
        @updatePagination="updatePagination"
      />
      <AppLoading
        v-if="isLoading"
        style="height: 300px"
      />
      <AppTable
        v-else
        class="pt-4"
        :rows="courses"
        resourceName="Courses"
        @select="goToCourse"
      >
        <template #header>
          <th class="th">Subject Code</th>
          <th class="th">{{ $localize('course') | capitalize }} Number</th>
          <th class="th">{{ $localize('course') | capitalize }} Name</th>
        </template>
        <template #default="{ row: course }">
          <td class="align-middle font-weight-bold">{{ course.subjectCode }}</td>
          <td class="align-middle font-weight-bold">{{ course.courseNumber }}</td>
          <td class="align-middle font-weight-bold">{{ course.name }}</td>
        </template>
      </AppTable>
    </div>
  </section>
</template>

<script>
import { getCmCourseSetCourses } from '@/api'
import { ref, provide } from '@vue/composition-api'
import usePagination from '@/components/composables/usePagination'
import FormText from '@/components/Forms/FormText'
import FormSelect from '@/components/Forms/FormSelect'
import FormCourseSelect from '@/components/Forms/FormCourseSelect'
import { mapGetters } from 'vuex'
import { required, requiredIf } from 'vuelidate/lib/validators'
import DynamicFilter from '@/components/DynamicFilter'
import { useCourseFilterFields } from '@/components/composables/useFilterFields'
import useDocumentTemplate from '@/components/composables/useDocumentTemplate'
import useDynamicFilters from '@/components/composables/useDynamicFilters'
import AsyncPagination from '@/components/AsyncPagination'
import LogicTree from '@/components/LogicTree'

export default {
  components: {
    FormText,
    FormSelect,
    FormCourseSelect,
    DynamicFilter,
    AsyncPagination,
    LogicTree,
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Object,
      default: () => ({}),
    },
    fetchMatchingCourses: {
      type: Array,
      default: () => ([]),
    },
  },
  setup (props, { root }) {
    const courses = ref({})
    const isLoading = ref(true)

    const { baseQuestionsArray, loadBaseTemplate, isLoading: isLoadingTemplate } = useDocumentTemplate({ type: ref('course') })
    const filterFields = useCourseFilterFields(baseQuestionsArray)

    const { pagination, updateLocalPagination } = usePagination({ root, limit: 10 })

    const effectiveDatesRange = ref('1800-01-01,2200-01-01')
    provide('effectiveDatesRange', effectiveDatesRange)
    provide('trackByGroupId', true)
    root.$store.commit('SET_EFFECTIVE_DATES_RANGE', effectiveDatesRange.value)

    const { getFilterById, fieldsFormatted } = useDynamicFilters(filterFields)

    return {
      courses,
      pagination,
      updatePagination: updateLocalPagination,
      loadBaseTemplate,
      filterFields,
      isLoadingTemplate,
      isLoading,
      getFilterById,
      fieldsFormatted,
    }
  },
  validations: {
    value: {
      name: { required },
      type: { required },
      structure: { required: requiredIf(model => [ 'static', 'dynamic' ].includes(model.type) && !model.filters) },
    },
  },
  computed: {
    ...mapGetters('curriculumCourses', [ 'cmCourseByEffectiveDatesRangeLookup' ]),
    courseSetOptions () {
      return [ 'static', 'dynamic' ]
    },
    courseSetStructure () {
      if (this.value.structure) return this.value.structure
      if (this.value.courseList && this.value.type === 'static') {
        return { condition: 'all', rules: this.value.courseList.map(courseId => ({ value: courseId })) }
      }
      if (this.value.filters && this.value.type === 'dynamic') {
        return { condition: this.value.condition === 'and' ? 'all' : 'any',
          rules: this.value.filters.map(el => ({
            ...el,
            id: el.id || `${el.name}-${el.group}`,
          })) }
      }
      return { condition: 'all', rules: [] }
    },
    newDynamicFilter () {
      const id = this.fieldsFormatted[0]?.id
      return { id, ...(this.getFilterById(id) || {}) }
    },
  },
  watch: {
    '$v.$invalid': {
      immediate: true,
      handler (value) {
        this.$emit('validation', this.$v.$invalid)
      },
    },
    'pagination.currentPage': {
      immediate: true,
      handler () {
        this.fetchCourses()
      },
    },
    fetchMatchingCourses () {
      if (!this.fetchMatchingCourses.length) return
      const page = this.pagination.currentPage
      this.updatePagination({ toPage: 1 })
      if (page === this.pagination.currentPage) this.fetchCourses()
    },
    'value.type': {
      handler () {
        this.update('structure', { condition: 'all', rules: [] })
      },
    },
  },
  async mounted () {
    this.loadBaseTemplate()
    this.$v.$touch()
  },
  methods: {
    update (key, value) {
      this.$v.$touch()
      this.$emit('input', { ...this.value, [key]: value })
    },
    goToCourse (course) {
      this.$router.push({ name: 'cmCourse', params: { courseId: course.id } })
    },
    async fetchCourses () {
      this.isLoading = true
      const courses = await getCmCourseSetCourses({
        school: this.$store.getters.school,
        courseSetId: this.value.id,
        ...(this.value.type === 'dynamic' ? {
          skip: this.pagination.skip,
          limit: this.pagination.limit,
        } : {}),
      })
      if (this.value.type === 'static') this.$store.commit('curriculumCourses/setCmCourses', courses)
      else this.courses = courses
      this.isLoading = false
    },
  },
}
</script>
